<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Report from 'dashboard/api/reports';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

ChartJS.register(Title, Tooltip, Filler, LineElement, PointElement, CategoryScale, LinearScale);

const labels = useMapGetter('labels/getLabels');
const rawData = ref([]);
const selectedOption = ref('__all__');
const lastUpdated = ref(null);
const dropdownOpen = ref(false);
const chartKey = ref(0);

const CHART_COLOR = '#4F46E5';

const dropdownOptions = computed(() => {
  const opts = [{ id: '__all__', title: 'Todas' }];
  (labels.value || []).forEach(l => opts.push({ id: l.id, title: l.title }));
  return opts;
});

const selectedTitle = computed(() => {
  const opt = dropdownOptions.value.find(o => o.id === selectedOption.value);
  return opt ? opt.title : 'Todas';
});

function aggregateByHour(items) {
  const hours = new Array(24).fill(0);
  if (!Array.isArray(items)) return hours;
  items.forEach(item => {
    if (item && typeof item.timestamp === 'number') {
      const hour = new Date(item.timestamp * 1000).getHours();
      hours[hour] += (item.value || 0);
    }
  });
  return hours;
}

const hourlyData = computed(() => aggregateByHour(rawData.value));

async function fetchData() {
  const now = new Date();
  const since = getUnixTime(startOfDay(subDays(now, 6)));
  const until = getUnixTime(endOfDay(now));
  try {
    if (selectedOption.value === '__all__') {
      // Sum data from ALL labels
      const allLabels = labels.value || [];
      if (!allLabels.length) {
        rawData.value = [];
        lastUpdated.value = new Date();
        chartKey.value++;
        return;
      }
      const results = await Promise.all(
        allLabels.map(l =>
          Report.getReports({
            metric: 'conversations_count',
            from: since,
            to: until,
            groupBy: 'hour',
            type: 'label',
            id: l.id,
          }).then(r => r.data || []).catch(() => [])
        )
      );
      // Merge: sum values by timestamp
      const merged = {};
      results.forEach(arr => {
        arr.forEach(item => {
          if (!merged[item.timestamp]) {
            merged[item.timestamp] = { value: 0, timestamp: item.timestamp };
          }
          merged[item.timestamp].value += item.value || 0;
        });
      });
      rawData.value = Object.values(merged).sort((a, b) => a.timestamp - b.timestamp);
    } else {
      const resp = await Report.getReports({
        metric: 'conversations_count',
        from: since,
        to: until,
        groupBy: 'hour',
        type: 'label',
        id: selectedOption.value,
      });
      rawData.value = resp.data || [];
    }
    lastUpdated.value = new Date();
    chartKey.value++;
  } catch {
    rawData.value = [];
  }
}

watch(selectedOption, () => { fetchData(); });
onMounted(() => { fetchData(); });

function selectOpt(id) {
  selectedOption.value = id;
  dropdownOpen.value = false;
}

const updatedText = computed(() => {
  if (!lastUpdated.value) return '';
  const diff = Math.round((Date.now() - lastUpdated.value.getTime()) / 60000);
  if (diff < 1) return 'Atualizado agora';
  return 'Atualizado ha ' + diff + 'min';
});

// Dark mode
const isDark = ref(document.body.classList.contains('dark'));
let darkObs = null;
onMounted(() => {
  darkObs = new MutationObserver(() => {
    isDark.value = document.body.classList.contains('dark');
    chartKey.value++;
  });
  darkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
});
onBeforeUnmount(() => { if (darkObs) darkObs.disconnect(); });

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.substring(0,2),16), parseInt(h.substring(2,4),16), parseInt(h.substring(4,6),16)].join(',');
}

const xLabels = Array.from({ length: 24 }, (_, i) => String(i));

const chartData = computed(() => ({
  labels: xLabels,
  datasets: [{
    label: 'Conversas',
    data: hourlyData.value,
    borderColor: CHART_COLOR,
    backgroundColor: (ctx) => {
      if (!ctx.chart.chartArea) return 'transparent';
      const { top, bottom } = ctx.chart.chartArea;
      const gradient = ctx.chart.ctx.createLinearGradient(0, top, 0, bottom);
      const rgb = hexToRgb(CHART_COLOR);
      gradient.addColorStop(0, 'rgba(' + rgb + ', 0.18)');
      gradient.addColorStop(1, 'rgba(' + rgb + ', 0)');
      return gradient;
    },
    fill: true,
    tension: 0.4,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: CHART_COLOR,
    pointHoverBorderColor: '#FFFFFF',
    pointHoverBorderWidth: 2,
  }],
}));

const chartOptions = computed(() => {
  const dark = isDark.value;
  const gridColor = dark ? 'rgba(255,255,255,0.06)' : '#F3F4F6';
  const tickColor = dark ? '#9CA3AF' : '#262626';
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#000',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 8,
        cornerRadius: 4,
        callbacks: { label: (ctx) => ' ' + ctx.parsed.y + ' conversa(s)' },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: tickColor, font: { size: 10, family: 'Inter, sans-serif' }, maxTicksLimit: 12 },
      },
      y: {
        beginAtZero: true,
        suggestedMax: Math.max(...hourlyData.value, 3) + 1,
        ticks: { color: tickColor, font: { size: 10, family: 'Inter, sans-serif' }, maxTicksLimit: 6, stepSize: Math.max(...hourlyData.value) <= 5 ? 1 : undefined },
        grid: { color: gridColor, lineWidth: 1 },
        border: { display: false },
      },
    },
  };
});

function downloadCSV() {
  const rows = hourlyData.value.map((v, i) => i + 'h,' + v);
  const csv = 'Hora,Conversas\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'etiquetas_' + selectedTitle.value + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}
</script>

<template>
  <div class="sz-etiquetas-card">
    <!-- Header -->
    <div class="sz-etiq-header">
      <div class="sz-etiq-title-col">
        <span class="sz-etiq-title">Etiquetas</span>
        <span class="sz-etiq-updated">{{ updatedText }}</span>
      </div>
      <div class="sz-etiq-controls">
        <!-- Dropdown -->
        <div class="sz-etiq-dropdown-wrap">
          <button class="sz-etiq-dropdown-btn" @click="dropdownOpen = !dropdownOpen">
            <span class="sz-etiq-dot" />
            <span class="sz-etiq-dropdown-text">{{ selectedTitle }}</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="sz-etiq-chevron" :class="{ open: dropdownOpen }">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div v-if="dropdownOpen" class="sz-etiq-dropdown-menu">
            <button
              v-for="opt in dropdownOptions"
              :key="opt.id"
              class="sz-etiq-dropdown-item"
              :class="{ active: opt.id === selectedOption }"
              @click="selectOpt(opt.id)"
            >
              {{ opt.title }}
            </button>
          </div>
        </div>
        <!-- Download -->
        <button class="sz-etiq-export-btn" @click="downloadCSV" title="Exportar CSV">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3.75 12.5V15.625C3.75 15.9565 3.8817 16.2745 4.11612 16.5089C4.35054 16.7433 4.66848 16.875 5 16.875H15C15.3315 16.875 15.6495 16.7433 15.8839 16.5089C16.1183 16.2745 16.25 15.9565 16.25 15.625V12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6.25 8.75L10 12.5L13.75 8.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 3.125V12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Chart -->
    <div class="sz-etiq-chart">
      <Line :key="chartKey" :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<style scoped>
.sz-etiquetas-card {
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  overflow: hidden;
}

.sz-etiq-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 12px 8px;
  min-height: 64px;
}

.sz-etiq-title-col {
  display: flex;
  flex-direction: column;
}

.sz-etiq-title {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
}

.sz-etiq-updated {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: #525252;
}

.sz-etiq-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.sz-etiq-dropdown-wrap { position: relative; }

.sz-etiq-dropdown-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: #FFFFFF;
  border: 1px solid #D4D4D4;
  border-radius: 8px;
  cursor: pointer;
  height: 32px;
}

.sz-etiq-dot {
  width: 12px;
  height: 12px;
  background: #4F46E5;
  border-radius: 8px;
  flex-shrink: 0;
}

.sz-etiq-dropdown-text {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: #000000;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sz-etiq-chevron { color: #000; transition: transform 0.2s; }
.sz-etiq-chevron.open { transform: rotate(180deg); }

.sz-etiq-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #D4D4D4;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 20;
  min-width: 180px;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;
}

.sz-etiq-dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #262626;
  background: none;
  border: none;
  cursor: pointer;
}
.sz-etiq-dropdown-item:hover { background: #F3F4F6; }
.sz-etiq-dropdown-item.active { background: #EEF2FF; color: #4F46E5; font-weight: 600; }

.sz-etiq-export-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1.5px solid #D4D4D4;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  color: #000;
}
.sz-etiq-export-btn:hover { background: #F3F4F6; }

.sz-etiq-chart {
  flex: 1;
  padding: 0 12px 12px;
  height: 300px;
  position: relative;
}

</style>

<style>
/* Dark mode */
.dark .sz-etiquetas-card { background: #1F2125; border-color: rgba(255,255,255,0.1); }
.dark .sz-etiq-title { color: #EDEEEF; }
.dark .sz-etiq-updated { color: #9CA3AF; }
.dark .sz-etiq-dropdown-btn { background: #1F2125; border-color: #363840; }
.dark .sz-etiq-dropdown-text { color: #EDEEEF; }
.dark .sz-etiq-chevron { color: #EDEEEF; }
.dark .sz-etiq-dropdown-menu { background: #2A2C30; border-color: #363840; }
.dark .sz-etiq-dropdown-item { color: #D4D4D4; }
.dark .sz-etiq-dropdown-item:hover { background: #363840; }
.dark .sz-etiq-dropdown-item.active { background: #312E81; color: #A5B4FC; }
.dark .sz-etiq-export-btn { color: #D4D4D4; border-color: #363840; }
.dark .sz-etiq-export-btn:hover { background: #363840; }
</style>
